import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const AdminSignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    adminCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
    setError('');
  };

  const validateForm = () => {
    if (!formData?.fullName || !formData?.email || !formData?.password || !formData?.adminCode) {
      setError('Por favor, completa todos los campos requeridos');
      return false;
    }

    if (formData?.password?.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    if (formData?.password !== formData?.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.email)) {
      setError('Por favor, ingresa un correo electrónico válido');
      return false;
    }

    // Admin code validation - use environment variable for security
    const validAdminCode = import.meta.env?.VITE_ADMIN_REGISTRATION_CODE || 'ADMIN2026';
    if (formData?.adminCode !== validAdminCode) {
      setError('Código de administrador inválido');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Step 1: Sign up the user
      const { data: signUpData, error: signUpError, requiresConfirmation, message } = await authService?.signUp(
        formData?.email,
        formData?.password,
        formData?.fullName,
        formData?.phone,
        'admin' // Force admin role
      );
      
      if (signUpError) {
        setError(signUpError?.message || 'Error al crear la cuenta de administrador');
        setLoading(false);
        return;
      }

      // If email confirmation is required
      if (requiresConfirmation) {
        setError('');
        // Show success message in the error state (we'll style it as success)
        setError(''); // Clear any previous errors
        // Create a success message
        const successMsg = message || 'Cuenta de administrador creada. Por favor, verifica tu correo electrónico.';
        // We need to add a success state
        setLoading(false);
        alert(successMsg + ' Serás redirigido a la página de inicio de sesión.');
        setTimeout(() => {
          navigate('/signin', { 
            replace: true,
            state: { 
              message: 'Revisa tu correo electrónico para confirmar tu cuenta de administrador antes de iniciar sesión.' 
            }
          });
        }, 2000);
        return;
      }

      if (signUpData?.user) {
        // Step 2: Automatically sign in
        const { data: signInData, error: signInError } = await authService?.signIn(
          formData?.email,
          formData?.password
        );

        if (signInError) {
          setError('Cuenta creada. Por favor, inicia sesión manualmente.');
          setLoading(false);
          setTimeout(() => navigate('/signin', { replace: true }), 2000);
          return;
        }

        // Step 3: Wait for profile creation and redirect
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max
        let isMounted = true;
        
        const checkProfile = setInterval(async () => {
          if (!isMounted) {
            clearInterval(checkProfile);
            return;
          }
          
          attempts++;
          const { data: profileData } = await authService?.getUserProfile(signInData?.user?.id);
          
          if (profileData && profileData?.role === 'admin') {
            clearInterval(checkProfile);
            if (isMounted) {
              navigate('/admin-dashboard', { replace: true });
            }
          } else if (attempts >= maxAttempts) {
            clearInterval(checkProfile);
            // Fallback to admin dashboard anyway since we set role to admin
            if (isMounted) {
              navigate('/admin-dashboard', { replace: true });
            }
          }
        }, 100);
        
        // Cleanup function
        return () => {
          isMounted = false;
          clearInterval(checkProfile);
        };
      }
    } catch (err) {
      setError('Error de conexión. Por favor, inténtalo de nuevo.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <Icon name="Shield" size={32} color="var(--color-primary)" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Registro de Administrador
          </h1>
          <p className="text-muted-foreground">
            Crea tu cuenta de administrador del sistema
          </p>
        </div>

        {/* Sign Up Form */}
        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error flex-1">{error}</p>
              </div>
            )}

            <div>
              <Input
                type="text"
                name="fullName"
                label="Nombre Completo"
                placeholder="Juan Pérez"
                value={formData?.fullName}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <Input
                type="email"
                name="email"
                label="Correo Electrónico"
                placeholder="admin@residencial3volcanes.com"
                value={formData?.email}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <Input
                type="tel"
                name="phone"
                label="Teléfono"
                placeholder="+51 987 654 321"
                value={formData?.phone}
                onChange={handleChange}
              />
            </div>

            <div>
              <Input
                type="text"
                name="adminCode"
                label="Código de Administrador"
                placeholder="Ingresa el código de administrador"
                value={formData?.adminCode}
                onChange={handleChange}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Código requerido para crear cuentas de administrador
              </p>
            </div>

            <div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label="Contraseña"
                  placeholder="••••••••"
                  value={formData?.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
            </div>

            <div>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  label="Confirmar Contraseña"
                  placeholder="••••••••"
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
              size="lg"
            >
              Crear Cuenta de Administrador
            </Button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              ¿Ya tienes cuenta?{' '}
              <Link to="/signin" className="text-primary hover:underline font-medium">
                Inicia sesión
              </Link>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              ¿Eres asesor?{' '}
              <Link to="/signup" className="text-primary hover:underline font-medium">
                Regístrate como asesor
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSignUp;